/**
 * DesignControlsPanel (iter-81)
 * Inline right column, always visible next to Manual Entry / Live Preview.
 *
 * 18 controls (sizes, spacings, alignments, fonts, line-height, image scale)
 * plus:
 *   - Reset to defaults
 *   - Save current values as a named Template
 *   - Apply a saved Template (dropdown)
 *
 * State strategy:
 *   - `values`  → the *pending* design (local edits, not yet saved to DB)
 *   - `saved`   → the last committed values (from server)
 *   - Debounced auto-save 700ms after last change.
 *   - Parent gets an onLiveChange callback so BookPreviewPane can
 *     mirror the design without a round trip.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Save, RotateCcw, Layers, Trash2, Check, ChevronRight, Palette, Sparkles, Lock, Unlock } from "lucide-react";

// ---------------------------------------------------------------------------
// Static option catalogue — mirrors design_controls.py. Kept here so
// the UI renders instantly without waiting on /api/design/defaults;
// server round-trip still happens so unknown keys are rejected.
// ---------------------------------------------------------------------------
const SIZE_OPTS    = [
  { v: "xs", label: "Extra Small" },
  { v: "s",  label: "Small" },
  { v: "m",  label: "Medium" },
  { v: "l",  label: "Large" },
  { v: "xl", label: "Extra Large" },
];
const SPACING_OPTS = [
  { v: "compact",  label: "Compact" },
  { v: "balanced", label: "Balanced" },
  { v: "generous", label: "Generous" },
];
const ALIGN_OPTS   = [
  { v: "left",    label: "Left" },
  { v: "center",  label: "Center" },
  { v: "right",   label: "Right" },
  { v: "justify", label: "Justify" },
];
const BODY_ALIGN_OPTS = ALIGN_OPTS.filter((a) => a.v !== "right"); // right is odd for body prose
const FONT_OPTS = [
  { v: "system",       label: "System UI" },
  { v: "georgia",      label: "Georgia" },
  { v: "merriweather", label: "Merriweather" },
  { v: "playfair",     label: "Playfair Display" },
  { v: "lora",         label: "Lora" },
  { v: "inter",        label: "Inter" },
  { v: "poppins",      label: "Poppins" },
  { v: "helvetica",    label: "Helvetica Neue" },
];
const LINE_HEIGHT_OPTS = [1.20, 1.35, 1.45, 1.55, 1.65, 1.80, 2.00];

const DEFAULTS = {
  book_title_size: "xl", book_title_align: "center", book_title_spacing: "balanced",
  subtitle_size: "m", subtitle_align: "center", subtitle_spacing: "balanced",
  body_font: "merriweather", heading_font: "playfair",
  body_size: "m", line_height: 1.55, body_align: "justify",
  paragraph_spacing: "balanced", chapter_spacing: "balanced",
  heading_spacing: "balanced", list_spacing: "balanced",
  chapter_label_size: "s", chapter_title_size: "l",
  chapter_subtitle_size: "m", chapter_subtitle_align: "center",
  // iter-110 — Paragraph first-line indent policy.
  //   none      — flush-left everywhere (new default)
  //   standard  — publisher convention (indent all, flush for the
  //               first paragraph of each chapter / after any heading)
  //   all       — indent every paragraph including the first
  paragraph_indent: "none",
  image_size: "m",
  // iter-97 — cover art footprint on the title page
  cover_size: "m",
  // iter-82 — cover + spine
  cover_title_font: "playfair", cover_title_size: "xl", cover_title_align: "center",
  cover_subtitle_font: "merriweather", cover_subtitle_size: "m",
  cover_author_font: "merriweather", cover_author_size: "s",
  spine_font: "playfair", spine_size: "s", spine_align: "center",
  // iter-83 — in-body heading scale
  h1_size: "l", h2_size: "m", h3_size: "s",
};

// iter-97 — Cover art footprint (Small / Medium / Large only). Bigger
// than xs/xl typography sizes because the cover is a physical inch
// pair, not a font multiplier.
const COVER_SIZE_OPTS = [
  { v: "s", label: "Small" },
  { v: "m", label: "Medium" },
  { v: "l", label: "Large" },
];

function pickStringKeys(obj) {
  // Server round-trips a ``_computed`` sub-object; strip it before saving.
  const clean = {};
  Object.keys(DEFAULTS).forEach((k) => { if (obj[k] !== undefined) clean[k] = obj[k]; });
  return clean;
}

function Row({ label, children, testid }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_120px] items-center gap-2 py-1.5"
         data-testid={testid}>
      <label className="text-[11px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e)=>onChange(e.target.value)}
            className="border border-[color:var(--border)] px-2 py-1 text-xs bg-white"
            data-testid={testid}>
      {options.map((o) => (
        <option key={o.v ?? o} value={o.v ?? o}>{o.label ?? o}</option>
      ))}
    </select>
  );
}

export function DesignControlsPanel({ bookId, initial, initialTemplateId, initialLocked, onLiveChange, onHide }) {
  const [values, setValues] = useState(() => ({ ...DEFAULTS, ...(pickStringKeys(initial || {})) }));
  // iter-97 — Explicit "Save design" workflow. Keep a snapshot of the
  // last-committed values so we can compute a dirty flag and drive the
  // Save button. The old 700ms per-change auto-save was replaced with:
  //   * one explicit `Save design` click → PATCHes once with everything
  //   * a silent 30s safety-net auto-save (only when dirty) so a
  //     closed tab never loses work
  const [savedValues, setSavedValues] = useState(() => ({ ...DEFAULTS, ...(pickStringKeys(initial || {})) }));
  const [templates, setTemplates] = useState([]);
  // iter-107 — Preselect the last-applied template so authors get a
  // visual confirmation that the design is still in place when they
  // reopen the book. The applyTemplate + backend PATCH set this
  // field on the book document via `design_template_id`.
  const [selectedTid, setSelectedTid] = useState(initialTemplateId || "");
  // iter-107 — Design Lock: when true every knob + template Apply /
  // Save is disabled AND the backend refuses writes with 409.
  const [designLocked, setDesignLocked] = useState(!!initialLocked);
  const [templateName, setTemplateName] = useState("");
  const [saving, setSaving] = useState(false);
  // iter-89 — Cover Auto-Palette suggestion (lazy-loaded on click)
  const [palette, setPalette] = useState(null);
  const [paletteBusy, setPaletteBusy] = useState(false);
  const safetyTimerRef = useRef();

  // Refresh from server on mount (so we get the resolved snapshot).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/design/templates");
        setTemplates(data?.templates || []);
      } catch (err) { /* silent */ }
    })();
  }, []);

  // Sync when the book changes (open a different book, or first mount).
  // iter-97 — we intentionally DO NOT re-sync on every `initial` change
  // because `onLiveChange` bounces back into `initial` via the parent
  // and would otherwise wipe local edits + reset the dirty flag on
  // every keystroke.
  useEffect(() => {
    if (initial && typeof initial === "object") {
      const merged = { ...DEFAULTS, ...pickStringKeys(initial) };
      setValues(merged);
      setSavedValues(merged);
    }
    // iter-107 — rehydrate the pre-selected template + lock flag
    // whenever the book changes.
    setSelectedTid(initialTemplateId || "");
    setDesignLocked(!!initialLocked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(savedValues),
    [values, savedValues]
  );

  // Persist the current `values` snapshot. Reused by the explicit
  // Save button, the safety-net auto-save, and Reset.
  const persist = useCallback(async (next, { silent = false } = {}) => {
    try {
      setSaving(true);
      await api.patch(`/books/${bookId}/design`, { values: next });
      setSavedValues(next);
      if (!silent) toast.success("Design saved");
      return true;
    } catch (err) {
      if (!silent) toast.error(formatApiError(err));
      return false;
    } finally {
      setSaving(false);
    }
  }, [bookId]);

  const set = useCallback((k, v) => {
    // iter-107 — Design Lock blocks every knob change.
    if (designLocked) {
      toast.error("Design is locked. Click the Locked button to unlock and edit.");
      return;
    }
    setValues((prev) => {
      const next = { ...prev, [k]: v };
      // Live preview mirrors immediately — no round-trip needed.
      if (typeof onLiveChange === "function") onLiveChange(next);
      return next;
    });
  }, [onLiveChange, designLocked]);

  // iter-111 — Save is always clickable; locked designs explain why.
  const saveNow = useCallback(async () => {
    if (saving) return;
    if (designLocked) {
      toast.error("Design is locked. Click the Locked button to unlock and edit.");
      return;
    }
    await persist(values);
  }, [saving, designLocked, persist, values]);

  // iter-111 — Debounced auto-save. Fires 1.2s after the last
  // edit if the panel is still dirty. Never toasts on success so the
  // author isn't nagged; a failed silent save falls back to the next
  // manual Save. iter-107 — skipped entirely while the design is
  // locked so a stale timer can't overwrite a frozen design.
  useEffect(() => {
    if (!isDirty || designLocked) return;
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    safetyTimerRef.current = setTimeout(() => {
      persist(values, { silent: true });
    }, 1200);
    return () => {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, [values, isDirty, persist, designLocked]);

  const reset = async () => {
    if (!window.confirm("Reset every design knob to the built-in defaults?")) return;
    setValues(DEFAULTS);
    if (typeof onLiveChange === "function") onLiveChange(DEFAULTS);
    await persist(DEFAULTS);
  };

  const saveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name) return toast.error("Give your template a name");
    try {
      const { data } = await api.post("/design/templates", { name, values });
      toast.success(`Template “${name}” saved`);
      setTemplateName("");
      const { data: list } = await api.get("/design/templates");
      setTemplates(list?.templates || []);
      setSelectedTid(data.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const applyTemplate = async (tid) => {
    if (!tid) return;
    if (designLocked) {
      toast.error("Design is locked. Click the Locked button to unlock and edit.");
      return;
    }
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    try {
      const { data } = await api.post(`/books/${bookId}/apply-design-template/${tid}`);
      const next = { ...DEFAULTS, ...pickStringKeys(data.design_controls || {}) };
      setValues(next);
      // The backend already persisted the template on this book, so
      // reflect that in the local baseline to keep the Save button
      // in "clean" state until the author edits something new.
      setSavedValues(next);
      // iter-107 — echo the server-persisted template id back so the
      // dropdown selection survives a reload.
      if (data?.design_template_id) setSelectedTid(data.design_template_id);
      if (typeof onLiveChange === "function") onLiveChange(next);
      toast.success(`Applied “${t.name}”`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // iter-107 — Design Lock toggle. Server-side write refusal is the
  // guarantee; disabling the UI is the courtesy so authors don't
  // hammer a knob that won't save.
  const toggleDesignLock = async () => {
    const next = !designLocked;
    try {
      await api.patch(`/books/${bookId}/design/lock`, { locked: next });
      setDesignLocked(next);
      toast.success(next ? "Design locked — knobs are read-only" : "Design unlocked — you can edit again");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteTemplate = async () => {
    if (!selectedTid) return;
    const t = templates.find((x) => x.id === selectedTid);
    if (!t) return;
    if (!window.confirm(`Delete template “${t.name}”?`)) return;
    try {
      await api.delete(`/design/templates/${selectedTid}`);
      setTemplates((prev) => prev.filter((x) => x.id !== selectedTid));
      setSelectedTid("");
      toast.success("Template deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // iter-89 — Cover Auto-Palette. Samples the uploaded cover and
  // recommends the preset whose typography best matches the cover mood.
  const suggestFromCover = async () => {
    setPaletteBusy(true);
    try {
      const { data } = await api.get(`/books/${bookId}/cover/palette`);
      setPalette(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setPaletteBusy(false); }
  };

  const applySuggestedPreset = async () => {
    const pid = palette?.suggestion?.preset_id;
    if (!pid) return;
    try {
      const { data } = await api.post(`/books/${bookId}/apply-design-template/${pid}`);
      const next = { ...DEFAULTS, ...pickStringKeys(data.design_controls || {}) };
      setValues(next);
      setSavedValues(next);
      if (typeof onLiveChange === "function") onLiveChange(next);
      toast.success(`Applied “${palette.suggestion.preset_name}”`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const dirty = isDirty;

  return (
    <div className="border border-[color:var(--border)] bg-white p-4 space-y-3"
         data-testid="design-controls-panel">
      <div>
        <p className="uppercase text-[10px] tracking-[0.25em] text-[color:var(--text-muted)]">§ Design controls</p>
        <div className="flex items-center justify-between mt-1 gap-2">
          <h3 className="font-editorial text-lg">Design</h3>
          <div className="flex items-center gap-2">
            {/* iter-107 — Design Lock toggle. Frozen = read-only for
                every knob + Apply + Save. Backend `PATCH /design` and
                `POST /apply-design-template/:tid` return 409 when
                this is on, so a stray auto-save can't unfreeze it. */}
            <button onClick={toggleDesignLock}
                    className={`text-xs inline-flex items-center gap-1 px-2 py-1 border transition-colors ${
                      designLocked
                        ? "border-[color:var(--primary)] text-[color:var(--primary)] bg-[color:var(--primary)]/10"
                        : "border-[color:var(--border)] text-[color:var(--text-muted)] hover:border-[color:var(--primary)] hover:text-[color:var(--primary)]"
                    }`}
                    title={designLocked ? "Unlock the design — allow edits again" : "Lock the design — freeze every knob"}
                    data-testid="design-lock-btn">
              {designLocked
                ? <><Lock className="w-3 h-3" strokeWidth={2}/> Locked</>
                : <><Unlock className="w-3 h-3" strokeWidth={1.5}/> Lock design</>}
            </button>
            {dirty && (
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-amber-100 text-amber-800"
                    data-testid="design-dirty-badge">
                Unsaved changes
              </span>
            )}
            <span className="text-[10px] text-[color:var(--text-muted)] uppercase tracking-widest"
                  data-testid="design-save-status">
              {saving ? "Saving…" : dirty ? "Not saved" : "Saved"}
            </span>
            <button onClick={saveNow}
                    disabled={saving}
                    className={`text-xs px-3 py-1 border transition-colors ${designLocked ? "border-[color:var(--border)] text-[color:var(--text-muted)]" : "border-[color:var(--primary)] text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-white"}`}
                    title={designLocked ? "Design is locked — unlock to save changes" : "Save the current design now"}
                    data-testid="design-save-btn">
              <Save className="inline w-3 h-3 mr-1" strokeWidth={1.5}/>
              {saving ? "Saving…" : "Save design"}
            </button>
            {onHide && (
              <button onClick={onHide}
                      className="text-[color:var(--text-muted)] hover:text-black p-1"
                      title="Hide the Design panel"
                      data-testid="design-hide-btn">
                <ChevronRight className="w-4 h-4" strokeWidth={1.5}/>
              </button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-[color:var(--text-muted)] mt-1">
          Every knob and template updates the live preview instantly and saves automatically.
          <strong> Save design</strong> forces an immediate save at any time.
        </p>
      </div>

      {/* ---- Templates ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
          <Layers className="inline w-3 h-3 mr-1" strokeWidth={1.5}/> Templates
        </p>
        <div className="flex gap-2">
          <select value={selectedTid} disabled={designLocked}
                  onChange={(e)=>{ setSelectedTid(e.target.value); applyTemplate(e.target.value); }}
                  className="flex-1 border border-[color:var(--border)] px-2 py-1 text-xs bg-white"
                  data-testid="design-template-select">
            <option value="">— Pick a template —</option>
            {templates.filter(t=>t.source==="builtin").length > 0 && (
              <optgroup label="Built-in presets">
                {templates.filter(t=>t.source==="builtin").map((t) => (
                  <option key={t.id} value={t.id}>★ {t.name}</option>
                ))}
              </optgroup>
            )}
            {templates.filter(t=>!t.source || t.source==="mine").length > 0 && (
              <optgroup label="My templates">
                {templates.filter(t=>!t.source || t.source==="mine").map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </optgroup>
            )}
            {templates.filter(t=>t.source==="shared").length > 0 && (
              <optgroup label="Shared by teammates">
                {templates.filter(t=>t.source==="shared").map((t) => (
                  <option key={t.id} value={t.id}>{t.name} · {t.owner_label || "Team"}</option>
                ))}
              </optgroup>
            )}
          </select>
          <button onClick={deleteTemplate}
                  disabled={!selectedTid || (() => {
                    const t = templates.find((x) => x.id === selectedTid);
                    return !t || t.source === "builtin" || t.source === "shared";
                  })()}
                  className="text-red-700 text-xs px-1 disabled:opacity-30"
                  title="Delete template (own templates only)"
                  data-testid="design-template-delete">
            <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
          </button>
        </div>
        <div className="flex gap-2 mt-2">
          <input value={templateName} onChange={(e)=>setTemplateName(e.target.value)}
                 placeholder="Save current as… (e.g. Novel Classic)"
                 className="flex-1 border border-[color:var(--border)] px-2 py-1 text-xs"
                 data-testid="design-template-name"/>
          <button onClick={saveAsTemplate}
                  className="btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid="design-template-save">
            <Save className="w-3 h-3" strokeWidth={1.5}/> Save
          </button>
        </div>
      </div>

      {/* ---- iter-89: Cover Auto-Palette suggestion ---- */}
      <div className="border-t border-[color:var(--border)] pt-3"
           data-testid="design-cover-palette">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
          <Palette className="inline w-3 h-3 mr-1" strokeWidth={1.5}/> From your cover
        </p>
        {!palette ? (
          <button onClick={suggestFromCover} disabled={paletteBusy}
                  className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                  title="Sample the uploaded cover and suggest a matching preset"
                  data-testid="design-cover-palette-btn">
            <Sparkles className="w-3 h-3" strokeWidth={1.5}/>
            {paletteBusy ? "Analysing cover…" : "Suggest preset from cover"}
          </button>
        ) : (
          <div className="space-y-2" data-testid="design-cover-palette-result">
            <div className="flex gap-1" data-testid="design-cover-palette-swatches">
              {(palette.swatches || []).map((s, i) => (
                <span key={i}
                      className="w-6 h-6 border border-[color:var(--border)]"
                      style={{ background: s.hex }}
                      title={`${s.hex} · ${Math.round(s.share * 100)}%`}
                      data-testid={`design-cover-swatch-${i}`}/>
              ))}
            </div>
            <p className="text-[11px] text-[color:var(--text-muted)]"
               data-testid="design-cover-palette-reason">
              Suggested: <span className="font-medium text-[color:var(--text)]">
                {palette.suggestion?.preset_name}
              </span> — {palette.suggestion?.reason}
            </p>
            <div className="flex gap-2">
              <button onClick={applySuggestedPreset}
                      className="btn-primary text-xs inline-flex items-center gap-1"
                      data-testid="design-cover-palette-apply">
                <Check className="w-3 h-3" strokeWidth={2}/>
                Apply {palette.suggestion?.preset_name}
              </button>
              <button onClick={()=>setPalette(null)}
                      className="btn-ghost text-xs"
                      data-testid="design-cover-palette-dismiss">
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---- Book title ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Book title</p>
        <Row label="Size" testid="dc-book-title-size">
          <Select value={values.book_title_size} onChange={(v)=>set("book_title_size", v)}
                  options={SIZE_OPTS} testid="dc-book-title-size-select"/>
        </Row>
        <Row label="Alignment" testid="dc-book-title-align">
          <Select value={values.book_title_align} onChange={(v)=>set("book_title_align", v)}
                  options={ALIGN_OPTS.filter(a=>a.v!=="justify")} testid="dc-book-title-align-select"/>
        </Row>
        <Row label="Spacing" testid="dc-book-title-spacing">
          <Select value={values.book_title_spacing} onChange={(v)=>set("book_title_spacing", v)}
                  options={SPACING_OPTS} testid="dc-book-title-spacing-select"/>
        </Row>
      </div>

      {/* ---- Subtitle ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Subtitle</p>
        <Row label="Size" testid="dc-subtitle-size">
          <Select value={values.subtitle_size} onChange={(v)=>set("subtitle_size", v)}
                  options={SIZE_OPTS} testid="dc-subtitle-size-select"/>
        </Row>
        <Row label="Alignment" testid="dc-subtitle-align">
          <Select value={values.subtitle_align} onChange={(v)=>set("subtitle_align", v)}
                  options={ALIGN_OPTS.filter(a=>a.v!=="justify")} testid="dc-subtitle-align-select"/>
        </Row>
        <Row label="Spacing" testid="dc-subtitle-spacing">
          <Select value={values.subtitle_spacing} onChange={(v)=>set("subtitle_spacing", v)}
                  options={SPACING_OPTS} testid="dc-subtitle-spacing-select"/>
        </Row>
      </div>

      {/* ---- Fonts + Body ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Fonts &amp; body</p>
        <Row label="Body font" testid="dc-body-font">
          <Select value={values.body_font} onChange={(v)=>set("body_font", v)}
                  options={FONT_OPTS} testid="dc-body-font-select"/>
        </Row>
        <Row label="Heading font" testid="dc-heading-font">
          <Select value={values.heading_font} onChange={(v)=>set("heading_font", v)}
                  options={FONT_OPTS} testid="dc-heading-font-select"/>
        </Row>
        <Row label="Body size" testid="dc-body-size">
          <Select value={values.body_size} onChange={(v)=>set("body_size", v)}
                  options={SIZE_OPTS} testid="dc-body-size-select"/>
        </Row>
        <Row label="Line height" testid="dc-line-height">
          <Select value={values.line_height}
                  onChange={(v)=>set("line_height", parseFloat(v))}
                  options={LINE_HEIGHT_OPTS.map((n)=>({v:n, label:n.toFixed(2)}))}
                  testid="dc-line-height-select"/>
        </Row>
        <Row label="Body alignment" testid="dc-body-align">
          <Select value={values.body_align} onChange={(v)=>set("body_align", v)}
                  options={BODY_ALIGN_OPTS} testid="dc-body-align-select"/>
        </Row>
      </div>

      {/* ---- Spacing ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Spacing</p>
        <Row label="Paragraph spacing" testid="dc-paragraph-spacing">
          <Select value={values.paragraph_spacing} onChange={(v)=>set("paragraph_spacing", v)}
                  options={SPACING_OPTS} testid="dc-paragraph-spacing-select"/>
        </Row>
        <Row label="Chapter spacing" testid="dc-chapter-spacing">
          <Select value={values.chapter_spacing} onChange={(v)=>set("chapter_spacing", v)}
                  options={SPACING_OPTS} testid="dc-chapter-spacing-select"/>
        </Row>
        <Row label="Heading spacing" testid="dc-heading-spacing">
          <Select value={values.heading_spacing} onChange={(v)=>set("heading_spacing", v)}
                  options={SPACING_OPTS} testid="dc-heading-spacing-select"/>
        </Row>
        <Row label="List spacing" testid="dc-list-spacing">
          <Select value={values.list_spacing} onChange={(v)=>set("list_spacing", v)}
                  options={SPACING_OPTS} testid="dc-list-spacing-select"/>
        </Row>
      </div>

      {/* ---- Chapter typography ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Chapter typography</p>
        <Row label="Chapter label size" testid="dc-chapter-label-size">
          <Select value={values.chapter_label_size} onChange={(v)=>set("chapter_label_size", v)}
                  options={SIZE_OPTS} testid="dc-chapter-label-size-select"/>
        </Row>
        <Row label="Chapter title size" testid="dc-chapter-title-size">
          <Select value={values.chapter_title_size} onChange={(v)=>set("chapter_title_size", v)}
                  options={SIZE_OPTS} testid="dc-chapter-title-size-select"/>
        </Row>
        <Row label="Chapter subtitle size" testid="dc-chapter-subtitle-size">
          <Select value={values.chapter_subtitle_size} onChange={(v)=>set("chapter_subtitle_size", v)}
                  options={SIZE_OPTS} testid="dc-chapter-subtitle-size-select"/>
        </Row>
        {/* iter-109 — Chapter subtitle alignment knob. Previously the
            in-body subtitle (e.g. "Chapter 3 · The Weight of the
            Nervous System") inherited the body `justify` alignment,
            which stretched a single-line subtitle oddly. */}
        <Row label="Chapter subtitle alignment" testid="dc-chapter-subtitle-align">
          <Select value={values.chapter_subtitle_align} onChange={(v)=>set("chapter_subtitle_align", v)}
                  options={ALIGN_OPTS} testid="dc-chapter-subtitle-align-select"/>
        </Row>
        {/* iter-110 — Paragraph first-line indent knob. */}
        <Row label="Paragraph indent" testid="dc-paragraph-indent">
          <Select value={values.paragraph_indent || "none"} onChange={(v)=>set("paragraph_indent", v)}
                  options={[
                    { v: "none",     label: "None (flush-left everywhere)" },
                    { v: "standard", label: "Standard (indent all · flush first after headings)" },
                    { v: "all",      label: "All paragraphs (indent even the first)" },
                  ]} testid="dc-paragraph-indent-select"/>
        </Row>
      </div>

      {/* ---- iter-83 In-body headings (H1, H2, H3) ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">In-body headings</p>
        <Row label="H1 size" testid="dc-h1-size">
          <Select value={values.h1_size} onChange={(v)=>set("h1_size", v)}
                  options={SIZE_OPTS.filter(o=>o.v!=="xl")} testid="dc-h1-size-select"/>
        </Row>
        <Row label="H2 size" testid="dc-h2-size">
          <Select value={values.h2_size} onChange={(v)=>set("h2_size", v)}
                  options={SIZE_OPTS.filter(o=>o.v!=="xl")} testid="dc-h2-size-select"/>
        </Row>
        <Row label="H3 size" testid="dc-h3-size">
          <Select value={values.h3_size} onChange={(v)=>set("h3_size", v)}
                  options={SIZE_OPTS.filter(o=>o.v!=="xl")} testid="dc-h3-size-select"/>
        </Row>
      </div>

      {/* ---- Images ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Images</p>
        <Row label="Image size" testid="dc-image-size">
          <Select value={values.image_size} onChange={(v)=>set("image_size", v)}
                  options={SIZE_OPTS} testid="dc-image-size-select"/>
        </Row>
      </div>

      {/* ---- iter-82 Cover ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Cover</p>
        {/* iter-97 — Cover art footprint on the title page. Small /
            Medium / Large maps to a physical inch pair so both the PDF
            export and the live preview stay in lockstep. */}
        <Row label="Cover size" testid="dc-cover-size">
          <Select value={values.cover_size} onChange={(v)=>set("cover_size", v)}
                  options={COVER_SIZE_OPTS} testid="dc-cover-size-select"/>
        </Row>
        <Row label="Title font" testid="dc-cover-title-font">
          <Select value={values.cover_title_font} onChange={(v)=>set("cover_title_font", v)}
                  options={FONT_OPTS} testid="dc-cover-title-font-select"/>
        </Row>
        <Row label="Title size" testid="dc-cover-title-size">
          <Select value={values.cover_title_size} onChange={(v)=>set("cover_title_size", v)}
                  options={SIZE_OPTS} testid="dc-cover-title-size-select"/>
        </Row>
        <Row label="Title alignment" testid="dc-cover-title-align">
          <Select value={values.cover_title_align} onChange={(v)=>set("cover_title_align", v)}
                  options={ALIGN_OPTS.filter(a=>a.v!=="justify")}
                  testid="dc-cover-title-align-select"/>
        </Row>
        <Row label="Subtitle font" testid="dc-cover-subtitle-font">
          <Select value={values.cover_subtitle_font} onChange={(v)=>set("cover_subtitle_font", v)}
                  options={FONT_OPTS} testid="dc-cover-subtitle-font-select"/>
        </Row>
        <Row label="Subtitle size" testid="dc-cover-subtitle-size">
          <Select value={values.cover_subtitle_size} onChange={(v)=>set("cover_subtitle_size", v)}
                  options={SIZE_OPTS} testid="dc-cover-subtitle-size-select"/>
        </Row>
        <Row label="Author font" testid="dc-cover-author-font">
          <Select value={values.cover_author_font} onChange={(v)=>set("cover_author_font", v)}
                  options={FONT_OPTS} testid="dc-cover-author-font-select"/>
        </Row>
        <Row label="Author size" testid="dc-cover-author-size">
          <Select value={values.cover_author_size} onChange={(v)=>set("cover_author_size", v)}
                  options={SIZE_OPTS} testid="dc-cover-author-size-select"/>
        </Row>
      </div>

      {/* ---- iter-82 Spine ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Spine</p>
        <Row label="Spine font" testid="dc-spine-font">
          <Select value={values.spine_font} onChange={(v)=>set("spine_font", v)}
                  options={FONT_OPTS} testid="dc-spine-font-select"/>
        </Row>
        <Row label="Spine size" testid="dc-spine-size">
          <Select value={values.spine_size} onChange={(v)=>set("spine_size", v)}
                  options={SIZE_OPTS} testid="dc-spine-size-select"/>
        </Row>
        <Row label="Spine alignment" testid="dc-spine-align">
          <Select value={values.spine_align} onChange={(v)=>set("spine_align", v)}
                  options={ALIGN_OPTS.filter(a=>a.v!=="justify")}
                  testid="dc-spine-align-select"/>
        </Row>
      </div>

      {/* ---- Reset ---- */}
      <div className="border-t border-[color:var(--border)] pt-3">
        <button onClick={reset}
                className="btn-ghost text-xs inline-flex items-center gap-1 w-full justify-center"
                data-testid="design-reset">
          <RotateCcw className="w-3 h-3" strokeWidth={1.5}/> Reset to preset defaults
        </button>
      </div>
    </div>
  );
}

export default DesignControlsPanel;
